import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTheme } from '../../hooks/useTheme';
import logo from '../../assets/Systemische Beratung in Dresden - Madlin Firme.png';

const ApproachSection = () => {
  const { features } = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });

  const steps = useMemo(() => [
    {
      title: 'Ankommen & Orientierung finden',
      description: 'In einem geschützten Rahmen finden Sie Raum für Ihre Gedanken und Gefühle. Wir beginnen dort, wo Sie gerade stehen und verschaffen uns gemeinsam einen Überblick.',
    },
    {
      title: 'Erkunden, was bewegt',
      description: 'Wir nehmen uns Zeit für das, was Sie aktuell beschäftigt, für Ihre Fragen, Gefühle und Anliegen. Dabei geht es darum, genau hinzuschauen und zu verstehen, was für Sie bedeutsam ist und erste Zusammenhänge wahrzunehmen.',
    },
    {
      title: 'Zusammenhänge verstehen',
      description: 'Gemeinsam ordnen wir Erfahrungen, Beziehungen und Muster ein, um besser zu verstehen, wie sich Situationen entwickelt haben und was sie beeinflusst.',
    },
    {
      title: 'Neue Perspektiven entwickeln',
      description: 'Auf dieser Grundlage entstehen neue Sichtweisen und mögliche nächste Schritte. Wir schauen gemeinsam, welche Veränderungen für Sie sinnvoll, hilfreich und stimmig sein können.',
    },
    {
      title: 'Veränderung gestalten',
      description: 'Sie entscheiden, welche Schritte Sie gehen möchten. Ich begleite Sie dabei, Veränderung bewusst umzusetzen und nachhaltig in Ihren Alltag zu integrieren.',
    },
  ], []);

  return (
    <section id="approach" className="py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Meine Arbeitsweise
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Wie bei einem Mobile entsteht Entwicklung durch Bewegung und Zusammenspiel. Schritte bauen aufeinander
              auf und wirken zugleich miteinander.
            </p>
            {/* ← section-divider: Farbe via --divider-color in index.css */}
            <div className="section-divider" />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* LEFT: Step Cards – gleiche Hover-Animation wie ServicesSection */}
            <div className="space-y-3">
              {steps.map((step, index) => {
                const isActive = activeStep === index;

                return (
                  <button
                    key={step.title}
                    type="button"
                    onClick={() => setActiveStep(index)}
                    className="w-full text-left"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {/*
                      card-hover liefert die Hover-Animation (translateY + scale + shadow)
                      Aktiver Zustand überschreibt border + shadow via inline-style
                    */}
                    <div
                      className="surface card-hover p-5"
                      style={{
                        borderRadius: 'var(--radius-xl)',
                        borderColor: isActive ? 'var(--color-border-strong)' : 'var(--color-border-subtle)',
                        boxShadow: isActive ? 'var(--shadow-md)' : 'var(--shadow-sm)',
                        transform: isActive ? 'translateY(-2px)' : undefined,
                        position: 'relative',
                        overflow: 'hidden',
                        backgroundColor: 'var(--color-surface-1)',
                      }}
                    >
                      {/* Linke Akzentleiste */}
                      <div
                        style={{
                          position: 'absolute',
                          left: 0, top: 0, bottom: 0,
                          width: 4,
                          backgroundColor: isActive ? 'var(--color-accent-500)' : 'var(--color-secondary-500)',
                          opacity: isActive ? 0.9 : 0.35,
                          transition: 'background-color 160ms ease, opacity 160ms ease',
                        }}
                      />

                      <div className="flex items-start gap-4">

                        <div className="flex-1">
                          <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                            {index + 1}. {step.title}
                          </h3>
                          <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* RIGHT: Logo */}
            <div className="relative h-full flex items-stretch">
              <div
                className="surface p-8 md:p-12 flex items-center justify-center w-full"
                style={{
                  borderRadius: 'var(--radius-xl)',
                  backgroundColor: '#ffffff',
                  minHeight: '440px',
                }}
              >
                <img
                  src={logo}
                  alt="Logo"
                  className="max-w-[280px] h-auto object-contain"
                  style={{ display: 'block', opacity: 1 }}
                />
              </div>
            </div>
          </div>

          {/* Werte */}
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Zugewandt',
                text: 'Ich begegne Ihnen mit echtem Interesse, Aufmerksamkeit und Respekt für Ihre Geschichte und Ihr Erleben.',
                icon: 'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z',
                chip: 'var(--color-primary-500)',
              },
              {
                title: 'Systemisch',
                text: 'Wir schauen darauf, wie innere Themen und äußere Beziehungen miteinander zusammenhängen.',
                icon: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z',
                chip: 'var(--color-secondary-500)',
              },
              {
                title: 'Stärkend',
                text: 'Ich unterstütze Sie dabei, eigene Fähigkeiten, innere Klarheit und Handlungsspielräume wieder zugänglich zu machen.',
                icon: 'M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z',
                chip: 'var(--color-accent-500)',
              },
            ].map((v) => (
              <div key={v.title} className="surface card-hover p-6" style={{ borderRadius: 'var(--radius-xl)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-full grid place-items-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-subtle)' }}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" style={{ color: v.chip }}>
                      <path fillRule="evenodd" d={v.icon} clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{v.title}</h4>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>{v.text}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ApproachSection;